import {DeviceType, LogLevel, Occupancy} from "@variocube/app-api";
import {
    BoxAccess,
    classNames,
    Dialog,
    DialogTitle,
    layout,
    MessageView,
    PaymentView,
    PushButton,
    typography,
    useBox,
    useCountdown,
    useDevicePresent,
    useIdle,
    usePlatform,
    usePreventDeactivate
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {getOccupancyOrder, Shops} from "./types";
import {defined} from "@variocube/app-common";
import {logger} from "./logger";
import {useLocalization} from "../i18n";

const log = logger.sub("pickup");

interface PickupProps {
    occupancy: Occupancy;
    shops: Shops;
    onDone: () => void;
}

enum State {
    OnlinePayment = "online",
    LocalPayment = "local",
    Pickup = "pickup"
}

export function PickupFlow({occupancy, shops, onDone}: PickupProps) {
    usePreventDeactivate();
    const platform = usePlatform();
    const paymentTerminal = useDevicePresent(DeviceType.PaymentTerminal);
    const {t, language} = useLocalization();
    const box = useBox(occupancy.boxNumber);
    const idle = useIdle(60);
    const left = useCountdown(30, idle);
    const [state, setState] = useState<State>();

    const {shop, order} = useMemo(() => {
        const occupancyOrder = getOccupancyOrder(occupancy);
        for (const shop of Object.values(shops)) {
            const order = shop.orders.find(order => occupancyOrder?.id == order.id);
            if (order) {
                return {shop, order};
            }
        }
        return {};
    }, [shops, occupancy]);

    const manualConfirm = Boolean(shop?.customersManuallyConfirmPickup);


    useEffect(() => {
        if (order) {
            if (!order.amount || !!order.paymentDate || order.allowPickupDate) {
                setState(State.Pickup);
            }
            else if (paymentTerminal && defined(order.amount)) {
                setState(State.LocalPayment);
            }
            else {
                setState(State.OnlinePayment);
            }
        }
    }, [order, paymentTerminal]);

    useEffect(() => {
        if (manualConfirm && idle && left <= 0) {
            onDone();
        }
    }, [manualConfirm, idle, left]);

    const handleConfirm = useCallback(async () => {
        onDone();
        try {
            log.info("Ending occupancy", occupancy);
            await platform.endOccupancy(occupancy.uuid);
            await platform.log(LogLevel.Audit, "Pickup", occupancy);
        } catch (error) {
            log.error("Error ending occupancy", error);
        }
    }, [occupancy, platform, onDone]);

    const handleVend = useCallback((payment) => platform.updateOccupancy(occupancy.uuid, {
        paymentDate: new Date().toISOString(),
        payment,
    }, true), [platform, occupancy]);

    const handlePaymentConfirmed = useCallback(() => setState(State.Pickup), []);

    if (order && box && state) {
        switch (state) {
            case State.OnlinePayment:
                return (
                    <MessageView message={t("CannotPickupOrder")}>
                        <div className={classNames(layout.fill, layout.horizontal, layout.center)}>
                            <PushButton onClick={onDone}>
                                {t("Close")}
                            </PushButton>
                        </div>
                    </MessageView>
                );
            case State.LocalPayment:
                return (
                    <PaymentView
                        amount={order.amount!}
                        currency={"EUR"}
                        strings={t}
                        onVend={handleVend}
                        onConfirmed={handlePaymentConfirmed}
                        language={language}
                        onCancel={onDone}
                    />
                );

            case State.Pickup:
                return (
                    <Fragment>
                        <BoxAccess
                            boxes={box}
                            platform={platform}
                            onCancel={onDone}
                            onConfirm={handleConfirm}
                            autoConfirm={!manualConfirm}
                            timeout={!manualConfirm}
                            boxOpenTitle={t("PickupTitle")}
                            boxOpenMessage={t("PickupMessage", { boxNames: box.number })}
                            confirmTitle={t("PickupConfirmTitle")}
                            confirmMessage={t("PickupConfirmMessage", {boxNames: box.number})}
                            strings={t}
                        />
                        <Dialog open={manualConfirm && idle}>
                            <DialogTitle>{t("AutoCancelPickupTitle")}</DialogTitle>
                            <div className={layout.paddingMedium}>
                                {t("AutoCancelPickupMessage", {remainingSeconds: left})}
                            </div>
                            <div className={classNames(layout.paddingMedium, typography.alignCenter)}>
                                <PushButton>{t("ContinuePickup")}</PushButton>
                            </div>
                        </Dialog>
                    </Fragment>
                );
        }
    }

    return null;
}


